// @ts-check
import { UPLINK } from '../game/uplink.js';
const clamp = (/** @type {number} */ n) => Math.max(0, Math.min(1, n));
/** Derive presentation from the session clock without changing player physics.
 * @param {import('../game/session.js').Session} session @param {boolean} reduced @param {number} bodyWidth
 */
export function uplinkVisual(session, reduced, bodyWidth) {
  const t = session.transfer;
  const approach = t?.approach ?? UPLINK.approach;
  const result = { x: session.player.x, y: session.player.y, opacity: 1, scale: 1, dissolve: 0, cover: 0, walking: false, age: t?.age ?? 0 };
  if (!t) { if (session.mode === 'level' && session.level.status === 'won') result.opacity = 0; return result; }
  if (t.phase === 'arriving') {
    const p = clamp(t.age / UPLINK.arrival);
    result.cover = 1 - clamp(p / 0.7);
    result.dissolve = reduced ? 0 : 1 - p;
    result.opacity = p;
    if (t.quantum && !reduced) {
      result.y += (1 - p) * 14;
      // The camera is now inside the tiny space: finish shrinking from a larger silhouette.
      result.scale = session.level.definition.quantum ? 1 + .85 * (1 - p) ** 2 : .65 + .35 * p;
      if (session.level.definition.quantum) {
        result.opacity = clamp(p * 2.5);
        result.dissolve = 1 - clamp(p * 2);
      }
    }
  } else {
    const walk = clamp(t.age / approach);
    const dissolve = clamp((t.age - approach) / UPLINK.dissolve);
    result.x = t.quantum ? t.x : t.x + (t.gate - bodyWidth / 2 - t.x) * walk;
    result.y = t.y + (t.quantum && !reduced ? Math.sin(walk * Math.PI / 2) * 14 : 0);
    result.walking = !t.quantum && walk < 1;
    result.dissolve = reduced ? 0 : dissolve;
    result.opacity = reduced ? 1 - dissolve : dissolve < 1 ? 1 - dissolve * 0.35 : 0;
    const rescaling = t.quantum && session.level.definition.quantum && !session.level.definition.quantumFinale;
    const shrinking = !session.level.definition.quantum && (t.quantum || session.nextLevel?.quantum);
    // Pipe entry begins shrinking immediately. A campaign finale keeps its victory walk first.
    const shrink = clamp((t.age - (t.quantum ? 0 : approach)) / (t.quantum ? approach + UPLINK.dissolve : UPLINK.dissolve));
    result.scale = reduced ? 1 : rescaling ? 1 + dissolve * .45 : shrinking ? 1 - .78 * shrink * shrink * (3 - 2 * shrink) : 1 - dissolve * .2;
    result.cover = clamp((t.age - approach - UPLINK.dissolve - 0.1) / 0.45);
  }
  return result;
}

/** Foreground packets and scan light pass in front of QP's unmodified artwork.
 * @param {HTMLCanvasElement} canvas
 */
export function createUplinkRenderer(canvas) {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Uplink Canvas is unavailable');
  const style = window.getComputedStyle(document.documentElement);
  const cyan = style.getPropertyValue('--color-glow-cyan').trim();
  const violet = style.getPropertyValue('--color-space-violet').trim();
  const light = style.getPropertyValue('--color-surface-12').trim();
  return {
    /** @param {import('../game/uplink.js').Transfer|null} transfer
     * @param {{width:number,height:number,x:number,feet:number,scale:number,artHeight:number}} view @param {boolean} reduced
     */
    render(transfer, view, reduced) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== Math.round(view.width * dpr) || canvas.height !== Math.round(view.height * dpr)) {
        canvas.width = Math.round(view.width * dpr); canvas.height = Math.round(view.height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, view.width, view.height);
      if (!transfer || reduced) return;
      const arriving = transfer.phase === 'arriving';
      const p = clamp(arriving ? transfer.age / UPLINK.arrival : (transfer.age - (transfer.approach ?? UPLINK.approach)) / UPLINK.dissolve);
      const intensity = Math.sin(p * Math.PI);
      if (intensity <= 0) return;
      ctx.save(); ctx.translate(view.x, view.feet); ctx.scale(view.scale, view.scale);
      const scanY = -view.artHeight * (arriving ? 1 - p : p);
      ctx.shadowColor = cyan; ctx.shadowBlur = 12 * intensity;
      const glow = ctx.createLinearGradient(-36, 0, 36, 0);
      glow.addColorStop(0, 'transparent'); glow.addColorStop(0.5, cyan); glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow; ctx.globalAlpha = intensity * 0.8; ctx.fillRect(-36, scanY - 4, 72, 8);
      ctx.fillStyle = light; ctx.fillRect(-22, scanY - 0.7, 44, 1.4);
      for (let i = 0; i < 36; i++) {
        const travel = (p * 2.4 + i * 0.137) % 1;
        const spread = ((i * 17 % 43) - 21) * (1 - travel);
        const y = scanY - travel * (75 + i % 5 * 10);
        ctx.globalAlpha = intensity * (1 - travel);
        ctx.fillStyle = i % 4 === 0 ? violet : i % 3 === 0 ? light : cyan;
        ctx.fillRect(spread, y, i % 3 + 1.5, i % 2 + 2);
      }
      // A bounded signal bloom fills the destination sky without a full-screen flash.
      if (!arriving && transfer.spectacle) {
        ctx.globalAlpha = intensity * .55; ctx.lineWidth = 1.5;
        for (let ring = 0; ring < 3; ring++) {
          ctx.strokeStyle = ring === 1 ? violet : cyan;
          ctx.beginPath(); ctx.ellipse(0, -110 - p * 75, 25 + p * (150 + ring * 40), 12 + p * (35 + ring * 12), -ring * .25, 0, Math.PI * 2); ctx.stroke();
        }
        for (let n = 0; n < 48; n++) {
          const angle = n * Math.PI * 2 / 48, radius = 35 + p * (95 + n % 4 * 24);
          ctx.fillStyle = n % 3 ? cyan : light; ctx.globalAlpha = intensity * .8;
          ctx.fillRect(Math.cos(angle) * radius, -150 + Math.sin(angle) * radius * .65 - p * 50, n % 4 + 1, 3);
        }
        ctx.strokeStyle = light; ctx.globalAlpha = intensity * .5;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -view.height / view.scale); ctx.stroke();
      }
      ctx.restore();
    },
  };
}
